import PropTypes from 'prop-types';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import CardHeader from '@mui/material/CardHeader';
import Card from '@mui/material/Card';
import { Box } from '@mui/system';
import { CardContent, Typography } from '@mui/material';
// utils
import { fNumber } from '../../../utils/format-number';
// components
import Chart, { useChart } from '../../../components/chart';
// ----------------------------------------------------------------------

const CHART_HEIGHT = 400;
const LEGEND_HEIGHT = 72;
const CHART_AREA_HEIGHT = CHART_HEIGHT - LEGEND_HEIGHT;

const ChartContainer = styled(Box)({
  position: 'relative',
  height: CHART_HEIGHT,
  width: '100%',
});

const LegendContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: LEGEND_HEIGHT,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderTop: `dashed 1px ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  zIndex: 10,
  padding: theme.spacing(1, 0),
}));

const StyledChart = styled(Chart)({
  height: `${CHART_AREA_HEIGHT}px !important`,
  width: '100% !important',
  '& .apexcharts-canvas, .apexcharts-inner, svg, foreignObject': {
    height: `${CHART_AREA_HEIGHT}px !important`,
    width: `100% !important`,
  },
  '& .apexcharts-legend': {
    display: 'none !important',
  },
});

// ----------------------------------------------------------------------

export default function ChartProfile({ title, subheader, total, chart, ...other }) {
  const theme = useTheme();

  const {
    colors = theme.palette.simplai.array,
    series,
    options,
  } = chart;

  const chartSeries = series.map((i) => i.value);

  const chartOptions = useChart({
    colors: colors.constructor === Array ? colors : [colors],
    chart: {
      sparkline: {
        enabled: true,
      },
    },
    labels: series.map((i) => i.label),
    legend: {
      show: false, // Disable built-in legend
    },
    fill: {
      type: 'solid',
    },
    plotOptions: {
      radialBar: {
        hollow: { size: '40%' },
        dataLabels: {
          value: { offsetY: 8 },
          total: {
            show: false,
          },
        },
      },
    },
    ...options,
  });

  return (
    <Card {...other}>
      <CardContent>
        <Typography variant="h3" fontWeight="bold" align='center' marginBottom={2}>{title}</Typography>
        <ChartContainer>
          <StyledChart
            dir="ltr"
            type="radialBar"
            series={chartSeries}
            options={chartOptions}
            height={CHART_AREA_HEIGHT}
          />
          <LegendContainer>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 2, 
              justifyContent: 'center',
              maxWidth: '100%',
            }}>
              {series.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box 
                    sx={{ 
                      width: 12, 
                      height: 12, 
                      backgroundColor: colors[index] || theme.palette.primary.main,
                      borderRadius: '50%' 
                    }} 
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </LegendContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

ChartProfile.propTypes = {
  chart: PropTypes.object,
  subheader: PropTypes.string,
  title: PropTypes.string,
  total: PropTypes.number,
};
